import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { CalendarEventDto } from '@/types/calendar';

interface WorkoutComponent {
  name: string;
  type?: string;
  totalAmount?: number | null;
  unit?: string | null;
  intensity?: string | null;
  caloriesBurn?: { perUnit?: number } | null;
}

interface WorkoutDetails {
  rawInput?: string;
  totals?: { kcal?: number | null } | null;
  components?: WorkoutComponent[];
  // legacy fields
  durationMinutes?: number;
  workoutType?: string;
  notes?: string;
  calc?: { cal?: number };
}

export const WorkoutEventDetails: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const details = (event.details ?? {}) as WorkoutDetails;
  const { rawInput, totals, components, durationMinutes, workoutType, notes, calc } = details;

  const hasAiData = totals != null || (components && components.length > 0);

  if (!rawInput && !hasAiData && !durationMinutes && !workoutType && !notes && !calc?.cal) return null;

  return (
    <>
      {rawInput && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.4, mt: 0.5 }}>
          «{rawInput}»
        </Typography>
      )}

      {totals?.kcal != null && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{totals.kcal}</Typography>
              <Typography variant="caption" color="text.secondary">ккал</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Legacy: old-style events without totals */}
      {!hasAiData && (durationMinutes || calc?.cal) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {calc?.cal != null && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{calc.cal}</Typography>
                <Typography variant="caption" color="text.secondary">ккал</Typography>
              </Box>
            )}
            {durationMinutes != null && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{durationMinutes}</Typography>
                <Typography variant="caption" color="text.secondary">хв</Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {!!components?.length && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Склад
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {components.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption">{c.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                  {c.totalAmount != null && c.unit ? `${c.totalAmount} ${c.unit}` : c.totalAmount != null ? String(c.totalAmount) : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Legacy: workoutType / notes */}
      {!hasAiData && (workoutType || notes) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', alignItems: 'baseline' }}>
            {workoutType && (
              <>
                <Typography variant="body2" color="text.secondary">Тип</Typography>
                <Typography variant="body2">{workoutType}</Typography>
              </>
            )}
            {notes && (
              <>
                <Typography variant="body2" color="text.secondary">Нотатки</Typography>
                <Typography variant="body2">{notes}</Typography>
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
};
